import { createInstance } from 'i18next';

import type { AppLocale } from '../locale';

import { APP_LOCALES, getActiveInterfaceLocale } from '../locale';
import { ACCOUNT_TRANSLATIONS } from './account';
import { AUTH_TRANSLATIONS } from './auth';
import { BOOSTERS_TRANSLATIONS } from './boosters';
import { CREATE_PROJECT_TRANSLATIONS } from './createProject';
import { EDITOR_TRANSLATIONS } from './editor';
import { HOME_TRANSLATIONS } from './home';
import { HOME_HUB_TRANSLATIONS } from './homeHub';
import { PRICING_TRANSLATIONS } from './pricing';
import { PT_PT_TRANSLATIONS } from './pt-PT';
import { RECORDING_EDITOR_TRANSLATIONS } from './recordingEditor';
import { RECORDING_MODALS_TRANSLATIONS } from './recordingModals';
import { RECORDS_TRANSLATIONS } from './records';
import { SHELL_TRANSLATIONS } from './shell';
import { SIDEBAR_NAV_TRANSLATIONS } from './sidebarNav';
import { TRANSLATION_SECTION_TRANSLATIONS } from './translationSection';
import { TRIAL_TRANSLATIONS } from './trial';
import { TRIAL_PAYWALL_TRANSLATIONS } from './trialPaywall';
import { VOICE_TRANSLATIONS } from './voice';

export const EN_TRANSLATIONS = {
  'account.settings': 'Account settings',
  'common.advancedTools': 'Advanced tools',
  'common.back': 'Back',
  'common.desktopOnly': 'Desktop only',
  'common.fieldRequired': 'Field is required',
  'common.hideSidebar': 'Hide sidebar',
  'common.lockSidebar': 'Lock sidebar',
  'common.logout': 'Log out',
  'common.navigationSidebar': 'Navigation sidebar',
  'common.openSidebar': 'open sidebar',
  'common.or': 'or',
  'common.profile': 'Profile',
  'common.profileImage': 'Profile image',
  'common.serverError': 'Server error. Please try again later',
  'header.myProfile': 'My profile',
  'header.profileAndApi': 'Profile & API',
  'home.newFolder': 'New folder',
  'home.projects': 'Projects',
  'home.refreshPage': 'Refresh page',
  'home.searchVideos': 'Search your videos',
  'home.somethingWentWrong': 'Something went wrong',
  'home.translateVideoAndAudio': 'Translate video and audio',
  'home.trial.day': 'Day {currentDay} of {totalDays}',
  'home.tryRefresh': 'Please refresh the page',
  'language.english': 'English',
  'language.french': 'French',
  'language.italian': 'Italian',
  'language.label': 'Interface language',
  'language.portuguese': 'Portuguese (Brazil)',
  'language.portuguesePortugal': 'Portuguese (Portugal)',
  'language.russian': 'Russian',
  'language.spanish': 'Spanish',
  'sidebar.bookCall': 'Book a call with CS',
  'sidebar.dictionary': 'Dictionary',
  'sidebar.getHelp': 'Get help',
  'sidebar.helpCenter': 'Help Center',
  'sidebar.recordVideo': 'Record video',
  'sidebar.settingsMembers': 'Settings & members',
  'sidebar.translation': 'Translation',
  'sidebar.viewPlans': 'View plans',
  'sidebar.voiceCloneLibrary': 'Voice Clone Library',
  'createProject.autodetect': 'Autodetect',
  'createProject.choose': 'Choose',
  'createProject.chooseFile': 'Click to choose a file',
  'createProject.chooseFiles': 'Click to choose up to {count} files',
  'createProject.dragIt': 'or drag and drop it here',
  'createProject.dragThem': 'or drag and drop them here',
  'createProject.dropFiles': 'Drop your files or click to upload',
  'createProject.fillDetails': 'Fill in your project details',
  'createProject.fillDetailsAndUpload': 'Fill in your project details and upload files',
  'createProject.freeVideoLeft': '{count} free video',
  'createProject.freeVideosLeft': '{count} free videos',
  'createProject.localizeVideoTitle': 'Localize your video',
  'createProject.newTranslation': 'New translation',
  'createProject.noVideo': "Don't have a video?",
  'createProject.originalLanguage': 'Original language',
  'createProject.pasteLink': 'Paste a YouTube or Google Drive link',
  'createProject.pasteLinkWithVimeo': 'Paste a YouTube, Google Drive, or Vimeo link',
  'createProject.projectName': 'Project name',
  'createProject.renewPlan': 'Renew plan',
  'createProject.sampleDescription':
    "We've already filled in the details about the sample video. Just choose the language you want to translate the video into.",
  'createProject.selectLanguages': 'Select the language of the original video and the language you will translate into',
  'createProject.selectTargetLanguage': 'Select target language for video translation',
  'createProject.translate': 'Translate',
  'createProject.translateFirstMinute':
    "We'll translate the first minute of your video. Longer is fine - we'll use the first minute for now.",
  'createProject.translateRecording': 'Translate your screen record',
  'createProject.translateTo': 'Translate to',
  'createProject.trySampleVideo': 'Try a sample video',
  'createProject.upgradePlan': 'Upgrade plan',
  'createProject.uploadFile': 'Click to upload a file',
  'createProject.uploadTitle': 'Upload video or audio to translate',
  'createProject.videoLeft': '{count} video left',
  'createProject.videoName': 'Video name',
  'createProject.videoNames': 'Video names',
  'createProject.videosLeft': '{count} videos left',
  'createProject.videoSpeakers': 'Number of speakers in video',
  ...ACCOUNT_TRANSLATIONS.en,
  ...AUTH_TRANSLATIONS.en,
  ...BOOSTERS_TRANSLATIONS.en,
  ...CREATE_PROJECT_TRANSLATIONS.en,
  ...EDITOR_TRANSLATIONS.en,
  ...HOME_TRANSLATIONS.en,
  ...PRICING_TRANSLATIONS.en,
  ...RECORDING_EDITOR_TRANSLATIONS.en,
  ...RECORDING_MODALS_TRANSLATIONS.en,
  ...RECORDS_TRANSLATIONS.en,
  ...HOME_HUB_TRANSLATIONS.en,
  ...SIDEBAR_NAV_TRANSLATIONS.en,
  ...TRANSLATION_SECTION_TRANSLATIONS.en,
  ...SHELL_TRANSLATIONS.en,
  ...TRIAL_TRANSLATIONS.en,
  ...TRIAL_PAYWALL_TRANSLATIONS.en,
  ...VOICE_TRANSLATIONS.en,
} as const;

export type TranslationKey = keyof typeof EN_TRANSLATIONS;
export type Translate = (key: TranslationKey, variables?: Record<string, number | string>) => string;
type TranslationDictionary = Record<TranslationKey, string>;

const ES_TRANSLATIONS: TranslationDictionary = {
  'account.settings': 'Configuración de la cuenta',
  'common.advancedTools': 'Herramientas avanzadas',
  'common.back': 'Atrás',
  'common.desktopOnly': 'Solo en ordenador',
  'common.fieldRequired': 'Campo obligatorio',
  'common.hideSidebar': 'Ocultar barra lateral',
  'common.lockSidebar': 'Fijar barra lateral',
  'common.logout': 'Cerrar sesión',
  'common.navigationSidebar': 'Barra lateral de navegación',
  'common.openSidebar': 'Abrir barra lateral',
  'common.or': 'o',
  'common.profile': 'Perfil',
  'common.profileImage': 'Imagen de perfil',
  'common.serverError': 'Error del servidor. Inténtalo de nuevo más tarde',
  'header.myProfile': 'Mi perfil',
  'header.profileAndApi': 'Perfil y API',
  'home.newFolder': 'Nueva carpeta',
  'home.projects': 'Proyectos',
  'home.refreshPage': 'Actualizar página',
  'home.searchVideos': 'Buscar en tus videos',
  'home.somethingWentWrong': 'Algo salió mal',
  'home.translateVideoAndAudio': 'Traduce video y audio',
  'home.trial.day': 'Día {currentDay} de {totalDays}',
  'home.tryRefresh': 'Actualiza la página',
  'language.english': 'Inglés',
  'language.french': 'Francés',
  'language.italian': 'Italiano',
  'language.label': 'Idioma de la interfaz',
  'language.portuguese': 'Portugués (Brasil)',
  'language.portuguesePortugal': 'Portugués (Portugal)',
  'language.russian': 'Ruso',
  'language.spanish': 'Español',
  'sidebar.bookCall': 'Reservar una llamada con el equipo de Éxito del Cliente',
  'sidebar.dictionary': 'Diccionario',
  'sidebar.getHelp': 'Obtener ayuda',
  'sidebar.helpCenter': 'Centro de ayuda',
  'sidebar.recordVideo': 'Grabar vídeo',
  'sidebar.settingsMembers': 'Configuración y miembros',
  'sidebar.translation': 'Traducción',
  'sidebar.viewPlans': 'Ver planes',
  'sidebar.voiceCloneLibrary': 'Biblioteca de Voice Clone',
  'createProject.autodetect': 'Detección automática',
  'createProject.choose': 'Elegir',
  'createProject.chooseFile': 'Haz clic para elegir un archivo',
  'createProject.chooseFiles': 'Haz clic para elegir hasta {count} archivos',
  'createProject.dragIt': 'o arrástralo y suéltalo aquí',
  'createProject.dragThem': 'o arrástralos y suéltalos aquí',
  'createProject.dropFiles': 'Arrastra tus archivos o haz clic para subirlos',
  'createProject.fillDetails': 'Completa los detalles del proyecto',
  'createProject.fillDetailsAndUpload': 'Completa los detalles del proyecto y sube los archivos',
  'createProject.freeVideoLeft': '{count} video gratis',
  'createProject.freeVideosLeft': '{count} videos gratis',
  'createProject.localizeVideoTitle': 'Adapta tu video',
  'createProject.newTranslation': 'Nueva traducción',
  'createProject.noVideo': '¿No tienes un video?',
  'createProject.originalLanguage': 'Idioma original',
  'createProject.pasteLink': 'Pega un enlace de YouTube o Google Drive',
  'createProject.pasteLinkWithVimeo': 'Pega un enlace de YouTube, Google Drive o Vimeo',
  'createProject.projectName': 'Nombre del proyecto',
  'createProject.renewPlan': 'Renovar plan',
  'createProject.sampleDescription':
    'Ya hemos completado los detalles del video de muestra. Solo tienes que elegir el idioma al que quieres traducirlo.',
  'createProject.selectLanguages': 'Selecciona el idioma original y el idioma al que quieres traducir',
  'createProject.selectTargetLanguage': 'Selecciona el idioma de destino de la traducción',
  'createProject.translate': 'Traducir',
  'createProject.translateFirstMinute':
    'Traduciremos el primer minuto de tu video. No importa si es más largo: por ahora usaremos el primer minuto.',
  'createProject.translateRecording': 'Traduce tu grabación de pantalla',
  'createProject.translateTo': 'Traducir a',
  'createProject.trySampleVideo': 'Prueba con uno de nuestros ejemplos',
  'createProject.upgradePlan': 'Cambiar a un plan superior',
  'createProject.uploadFile': 'Haz clic para subir un archivo',
  'createProject.uploadTitle': 'Sube un video o audio para traducir',
  'createProject.videoLeft': 'Queda {count} video',
  'createProject.videoName': 'Nombre del video',
  'createProject.videoNames': 'Nombres de los videos',
  'createProject.videosLeft': 'Quedan {count} videos',
  'createProject.videoSpeakers': 'Número de hablantes en el video',
  ...ACCOUNT_TRANSLATIONS.es,
  ...AUTH_TRANSLATIONS.es,
  ...BOOSTERS_TRANSLATIONS.es,
  ...CREATE_PROJECT_TRANSLATIONS.es,
  ...EDITOR_TRANSLATIONS.es,
  ...HOME_TRANSLATIONS.es,
  ...PRICING_TRANSLATIONS.es,
  ...RECORDING_EDITOR_TRANSLATIONS.es,
  ...RECORDING_MODALS_TRANSLATIONS.es,
  ...RECORDS_TRANSLATIONS.es,
  ...HOME_HUB_TRANSLATIONS.es,
  ...SIDEBAR_NAV_TRANSLATIONS.es,
  ...TRANSLATION_SECTION_TRANSLATIONS.es,
  ...SHELL_TRANSLATIONS.es,
  ...TRIAL_TRANSLATIONS.es,
  ...TRIAL_PAYWALL_TRANSLATIONS.es,
  ...VOICE_TRANSLATIONS.es,
};

const PT_TRANSLATIONS: TranslationDictionary = {
  'account.settings': 'Configurações da conta',
  'common.advancedTools': 'Ferramentas avançadas',
  'common.back': 'Voltar',
  'common.desktopOnly': 'Apenas no computador',
  'common.fieldRequired': 'Campo obrigatório',
  'common.hideSidebar': 'Ocultar barra lateral',
  'common.lockSidebar': 'Fixar barra lateral',
  'common.logout': 'Sair',
  'common.navigationSidebar': 'Barra lateral de navegação',
  'common.openSidebar': 'Abrir barra lateral',
  'common.or': 'ou',
  'common.profile': 'Perfil',
  'common.profileImage': 'Imagem do perfil',
  'common.serverError': 'Erro no servidor. Tente novamente mais tarde',
  'header.myProfile': 'Meu perfil',
  'header.profileAndApi': 'Perfil e API',
  'home.newFolder': 'Nova pasta',
  'home.projects': 'Projetos',
  'home.refreshPage': 'Atualizar página',
  'home.searchVideos': 'Pesquisar seus vídeos',
  'home.somethingWentWrong': 'Algo deu errado',
  'home.translateVideoAndAudio': 'Traduzir vídeo e áudio',
  'home.trial.day': 'Dia {currentDay} de {totalDays}',
  'home.tryRefresh': 'Atualize a página',
  'language.english': 'Inglês',
  'language.french': 'Francês',
  'language.italian': 'Italiano',
  'language.label': 'Idioma da interface',
  'language.portuguese': 'Português (Brasil)',
  'language.portuguesePortugal': 'Português (Portugal)',
  'language.russian': 'Russo',
  'language.spanish': 'Espanhol',
  'sidebar.bookCall': 'Agendar uma chamada com a equipe de Sucesso do Cliente',
  'sidebar.dictionary': 'Dicionário',
  'sidebar.getHelp': 'Obter ajuda',
  'sidebar.helpCenter': 'Central de ajuda',
  'sidebar.recordVideo': 'Gravar vídeo',
  'sidebar.settingsMembers': 'Configurações e membros',
  'sidebar.translation': 'Tradução',
  'sidebar.viewPlans': 'Ver planos',
  'sidebar.voiceCloneLibrary': 'Biblioteca de Voice Clone',
  'createProject.autodetect': 'Detecção automática',
  'createProject.choose': 'Escolher',
  'createProject.chooseFile': 'Clique para escolher um arquivo',
  'createProject.chooseFiles': 'Clique para escolher até {count} arquivos',
  'createProject.dragIt': 'ou arraste-o e solte-o aqui',
  'createProject.dragThem': 'ou arraste-os e solte-os aqui',
  'createProject.dropFiles': 'Solte seus arquivos aqui ou clique para enviar',
  'createProject.fillDetails': 'Preencha os detalhes do projeto',
  'createProject.fillDetailsAndUpload': 'Preencha os detalhes do projeto e envie os arquivos',
  'createProject.freeVideoLeft': '{count} vídeo grátis',
  'createProject.freeVideosLeft': '{count} vídeos grátis',
  'createProject.localizeVideoTitle': 'Adapte seu vídeo',
  'createProject.newTranslation': 'Nova tradução',
  'createProject.noVideo': 'Não tem um vídeo?',
  'createProject.originalLanguage': 'Idioma original',
  'createProject.pasteLink': 'Adicione um link do YouTube ou Google Drive',
  'createProject.pasteLinkWithVimeo': 'Cole um link do YouTube, Google Drive ou Vimeo',
  'createProject.projectName': 'Nome do projeto',
  'createProject.renewPlan': 'Renovar plano',
  'createProject.sampleDescription':
    'Já preenchemos os detalhes do vídeo de exemplo. Basta escolher o idioma para o qual deseja traduzi-lo.',
  'createProject.selectLanguages': 'Selecione o idioma original e o idioma para o qual deseja traduzir',
  'createProject.selectTargetLanguage': 'Selecione o idioma de destino da tradução',
  'createProject.translate': 'Traduzir',
  'createProject.translateFirstMinute':
    'Vamos traduzir o primeiro minuto do seu vídeo. Não há problema se for mais longo: por enquanto, usaremos o primeiro minuto.',
  'createProject.translateRecording': 'Traduza sua gravação de tela',
  'createProject.translateTo': 'Traduzir para',
  'createProject.trySampleVideo': 'Teste um vídeo de exemplo',
  'createProject.upgradePlan': 'Fazer upgrade do plano',
  'createProject.uploadFile': 'Clique para enviar um arquivo',
  'createProject.uploadTitle': 'Envie um vídeo ou áudio para traduzir',
  'createProject.videoLeft': 'Resta {count} vídeo',
  'createProject.videoName': 'Nome do vídeo',
  'createProject.videoNames': 'Nomes dos vídeos',
  'createProject.videosLeft': 'Restam {count} vídeos',
  'createProject.videoSpeakers': 'Número de pessoas falando no vídeo',
  ...ACCOUNT_TRANSLATIONS.pt,
  ...AUTH_TRANSLATIONS.pt,
  ...BOOSTERS_TRANSLATIONS.pt,
  ...CREATE_PROJECT_TRANSLATIONS.pt,
  ...EDITOR_TRANSLATIONS.pt,
  ...HOME_TRANSLATIONS.pt,
  ...PRICING_TRANSLATIONS.pt,
  ...RECORDING_EDITOR_TRANSLATIONS.pt,
  ...RECORDING_MODALS_TRANSLATIONS.pt,
  ...RECORDS_TRANSLATIONS.pt,
  ...HOME_HUB_TRANSLATIONS.pt,
  ...SIDEBAR_NAV_TRANSLATIONS.pt,
  ...TRANSLATION_SECTION_TRANSLATIONS.pt,
  ...SHELL_TRANSLATIONS.pt,
  ...TRIAL_TRANSLATIONS.pt,
  ...TRIAL_PAYWALL_TRANSLATIONS.pt,
  ...VOICE_TRANSLATIONS.pt,
};

const FR_TRANSLATIONS: TranslationDictionary = {
  'account.settings': 'Paramètres du compte',
  'common.advancedTools': 'Outils avancés',
  'common.back': 'Retour',
  'common.desktopOnly': 'Sur ordinateur uniquement',
  'common.fieldRequired': 'Champ obligatoire',
  'common.hideSidebar': 'Masquer la barre latérale',
  'common.lockSidebar': 'Verrouiller la barre latérale',
  'common.logout': 'Se déconnecter',
  'common.navigationSidebar': 'Barre latérale de navigation',
  'common.openSidebar': 'Ouvrir la barre latérale',
  'common.or': 'ou',
  'common.profile': 'Profil',
  'common.profileImage': 'Image de profil',
  'common.serverError': 'Erreur du serveur. Réessayez plus tard',
  'header.myProfile': 'Mon profil',
  'header.profileAndApi': 'Profil et API',
  'home.newFolder': 'Nouveau dossier',
  'home.projects': 'Projets',
  'home.refreshPage': 'Actualiser la page',
  'home.searchVideos': 'Rechercher dans vos vidéos',
  'home.somethingWentWrong': "Une erreur s'est produite",
  'home.translateVideoAndAudio': 'Traduisez des vidéos et des fichiers audio',
  'home.trial.day': 'Jour {currentDay} sur {totalDays}',
  'home.tryRefresh': 'Actualisez la page',
  'language.english': 'Anglais',
  'language.french': 'Français',
  'language.italian': 'Italien',
  'language.label': "Langue de l'interface",
  'language.portuguese': 'Portugais (Brésil)',
  'language.portuguesePortugal': 'Portugais (Portugal)',
  'language.russian': 'Russe',
  'language.spanish': 'Espagnol',
  'sidebar.bookCall': "Planifier un appel avec l'équipe CS",
  'sidebar.dictionary': 'Dictionnaire',
  'sidebar.getHelp': "Obtenir de l'aide",
  'sidebar.helpCenter': "Centre d'aide",
  'sidebar.recordVideo': 'Enregistrer une vidéo',
  'sidebar.settingsMembers': 'Paramètres et membres',
  'sidebar.translation': 'Traduction',
  'sidebar.viewPlans': 'Voir les offres',
  'sidebar.voiceCloneLibrary': 'Bibliothèque de Voice Clone',
  'createProject.autodetect': 'Détection automatique',
  'createProject.choose': 'Choisir',
  'createProject.chooseFile': 'Cliquez pour choisir un fichier',
  'createProject.chooseFiles': "Cliquez pour choisir jusqu'à {count} fichiers",
  'createProject.dragIt': 'ou glissez-déposez-le ici',
  'createProject.dragThem': 'ou glissez-déposez-les ici',
  'createProject.dropFiles': 'Déposez vos fichiers ou cliquez pour les importer',
  'createProject.fillDetails': 'Renseignez les détails de votre projet',
  'createProject.fillDetailsAndUpload': 'Renseignez les détails du projet et importez vos fichiers',
  'createProject.freeVideoLeft': '{count} vidéo gratuite',
  'createProject.freeVideosLeft': '{count} vidéos gratuites',
  'createProject.localizeVideoTitle': 'Adaptez votre vidéo',
  'createProject.newTranslation': 'Nouvelle traduction',
  'createProject.noVideo': "Vous n'avez pas de vidéo ?",
  'createProject.originalLanguage': "Langue d'origine",
  'createProject.pasteLink': 'Collez un lien YouTube ou Google Drive',
  'createProject.pasteLinkWithVimeo': 'Collez un lien YouTube, Google Drive ou Vimeo',
  'createProject.projectName': 'Nom du projet',
  'createProject.renewPlan': "Renouveler l'offre",
  'createProject.sampleDescription':
    "Nous avons déjà renseigné les informations concernant la vidéo d'exemple. Choisissez simplement la langue dans laquelle vous souhaitez la traduire.",
  'createProject.selectLanguages': "Sélectionnez la langue d'origine et la langue de traduction",
  'createProject.selectTargetLanguage': 'Sélectionnez la langue cible de la traduction',
  'createProject.translate': 'Traduire',
  'createProject.translateFirstMinute':
    "Nous traduirons la première minute de votre vidéo. Votre vidéo peut être plus longue : pour le moment, nous n'utiliserons que la première minute.",
  'createProject.translateRecording': "Traduisez votre enregistrement d'écran",
  'createProject.translateTo': 'Traduire en',
  'createProject.trySampleVideo': "Essayez une vidéo d'exemple",
  'createProject.upgradePlan': 'Passer à une offre supérieure',
  'createProject.uploadFile': 'Cliquez pour importer un fichier',
  'createProject.uploadTitle': 'Importez une vidéo ou un fichier audio à traduire',
  'createProject.videoLeft': 'Il reste {count} vidéo',
  'createProject.videoName': 'Nom de la vidéo',
  'createProject.videoNames': 'Noms des vidéos',
  'createProject.videosLeft': 'Il reste {count} vidéos',
  'createProject.videoSpeakers': 'Nombre de locuteurs dans la vidéo',
  ...ACCOUNT_TRANSLATIONS.fr,
  ...AUTH_TRANSLATIONS.fr,
  ...BOOSTERS_TRANSLATIONS.fr,
  ...CREATE_PROJECT_TRANSLATIONS.fr,
  ...EDITOR_TRANSLATIONS.fr,
  ...HOME_TRANSLATIONS.fr,
  ...PRICING_TRANSLATIONS.fr,
  ...RECORDING_EDITOR_TRANSLATIONS.fr,
  ...RECORDING_MODALS_TRANSLATIONS.fr,
  ...RECORDS_TRANSLATIONS.fr,
  ...HOME_HUB_TRANSLATIONS.fr,
  ...SIDEBAR_NAV_TRANSLATIONS.fr,
  ...TRANSLATION_SECTION_TRANSLATIONS.fr,
  ...SHELL_TRANSLATIONS.fr,
  ...TRIAL_TRANSLATIONS.fr,
  ...TRIAL_PAYWALL_TRANSLATIONS.fr,
  ...VOICE_TRANSLATIONS.fr,
};

const IT_TRANSLATIONS: TranslationDictionary = {
  'account.settings': "Impostazioni dell'account",
  'common.advancedTools': 'Strumenti avanzati',
  'common.back': 'Indietro',
  'common.desktopOnly': 'Solo su computer',
  'common.fieldRequired': 'Campo obbligatorio',
  'common.hideSidebar': 'Nascondi barra laterale',
  'common.lockSidebar': 'Blocca barra laterale',
  'common.logout': 'Esci',
  'common.navigationSidebar': 'Barra laterale di navigazione',
  'common.openSidebar': 'Apri barra laterale',
  'common.or': 'o',
  'common.profile': 'Profilo',
  'common.profileImage': 'Immagine del profilo',
  'common.serverError': 'Errore del server. Riprova più tardi',
  'header.myProfile': 'Il mio profilo',
  'header.profileAndApi': 'Profilo e API',
  'home.newFolder': 'Nuova cartella',
  'home.projects': 'Progetti',
  'home.refreshPage': 'Aggiorna pagina',
  'home.searchVideos': 'Cerca nei tuoi video',
  'home.somethingWentWrong': 'Qualcosa è andato storto',
  'home.translateVideoAndAudio': 'Traduci video e audio',
  'home.trial.day': 'Giorno {currentDay} di {totalDays}',
  'home.tryRefresh': 'Aggiorna la pagina',
  'language.english': 'Inglese',
  'language.french': 'Francese',
  'language.italian': 'Italiano',
  'language.label': "Lingua dell'interfaccia",
  'language.portuguese': 'Portoghese (Brasile)',
  'language.portuguesePortugal': 'Portoghese (Portogallo)',
  'language.russian': 'Russo',
  'language.spanish': 'Spagnolo',
  'sidebar.bookCall': 'Prenota una chiamata con il team Customer Success',
  'sidebar.dictionary': 'Dizionario',
  'sidebar.getHelp': 'Richiedi assistenza',
  'sidebar.helpCenter': 'Centro assistenza',
  'sidebar.recordVideo': 'Registra video',
  'sidebar.settingsMembers': 'Impostazioni e membri',
  'sidebar.translation': 'Traduzione',
  'sidebar.viewPlans': 'Visualizza i piani',
  'sidebar.voiceCloneLibrary': 'Libreria Voice Clone',
  'createProject.autodetect': 'Rilevamento automatico',
  'createProject.choose': 'Scegli',
  'createProject.chooseFile': 'Fai clic per scegliere un file',
  'createProject.chooseFiles': 'Fai clic per scegliere fino a {count} file',
  'createProject.dragIt': 'oppure trascinalo qui',
  'createProject.dragThem': 'oppure trascinali qui',
  'createProject.dropFiles': 'Trascina i file o fai clic per caricarli',
  'createProject.fillDetails': 'Inserisci i dettagli del progetto',
  'createProject.fillDetailsAndUpload': 'Inserisci i dettagli del progetto e carica i file',
  'createProject.freeVideoLeft': '{count} video gratuito',
  'createProject.freeVideosLeft': '{count} video gratuiti',
  'createProject.localizeVideoTitle': 'Localizza il tuo video',
  'createProject.newTranslation': 'Nuova traduzione',
  'createProject.noVideo': 'Non hai un video?',
  'createProject.originalLanguage': 'Lingua originale',
  'createProject.pasteLink': 'Incolla un link di YouTube o Google Drive',
  'createProject.pasteLinkWithVimeo': 'Incolla un link di YouTube, Google Drive o Vimeo',
  'createProject.projectName': 'Nome del progetto',
  'createProject.renewPlan': 'Rinnova il piano',
  'createProject.sampleDescription':
    'Abbiamo già inserito i dettagli del video di esempio. Scegli la lingua in cui vuoi tradurlo.',
  'createProject.selectLanguages': 'Seleziona la lingua originale e la lingua in cui vuoi tradurre',
  'createProject.selectTargetLanguage': 'Seleziona la lingua di destinazione della traduzione',
  'createProject.translate': 'Traduci',
  'createProject.translateFirstMinute':
    'Tradurremo il primo minuto del tuo video. Può essere più lungo: per ora useremo il primo minuto.',
  'createProject.translateRecording': 'Traduci la registrazione dello schermo',
  'createProject.translateTo': 'Traduci in',
  'createProject.trySampleVideo': 'Prova un video di esempio',
  'createProject.upgradePlan': 'Passa a un piano superiore',
  'createProject.uploadFile': 'Fai clic per caricare un file',
  'createProject.uploadTitle': 'Carica un video o un audio da tradurre',
  'createProject.videoLeft': 'Rimane {count} video',
  'createProject.videoName': 'Nome del video',
  'createProject.videoNames': 'Nomi dei video',
  'createProject.videosLeft': 'Rimangono {count} video',
  'createProject.videoSpeakers': 'Numero di persone che parlano nel video',
  ...ACCOUNT_TRANSLATIONS.it,
  ...AUTH_TRANSLATIONS.it,
  ...BOOSTERS_TRANSLATIONS.it,
  ...CREATE_PROJECT_TRANSLATIONS.it,
  ...EDITOR_TRANSLATIONS.it,
  ...HOME_TRANSLATIONS.it,
  ...PRICING_TRANSLATIONS.it,
  ...RECORDING_EDITOR_TRANSLATIONS.it,
  ...RECORDING_MODALS_TRANSLATIONS.it,
  ...RECORDS_TRANSLATIONS.it,
  ...HOME_HUB_TRANSLATIONS.it,
  ...SIDEBAR_NAV_TRANSLATIONS.it,
  ...TRANSLATION_SECTION_TRANSLATIONS.it,
  ...SHELL_TRANSLATIONS.it,
  ...TRIAL_TRANSLATIONS.it,
  ...TRIAL_PAYWALL_TRANSLATIONS.it,
  ...VOICE_TRANSLATIONS.it,
};

const RU_TRANSLATIONS: TranslationDictionary = {
  'account.settings': 'Настройки аккаунта',
  'common.advancedTools': 'Дополнительные инструменты',
  'common.back': 'Назад',
  'common.desktopOnly': 'Только на компьютере',
  'common.fieldRequired': 'Обязательное поле',
  'common.hideSidebar': 'Скрыть боковую панель',
  'common.lockSidebar': 'Закрепить боковую панель',
  'common.logout': 'Выйти',
  'common.navigationSidebar': 'Панель навигации',
  'common.openSidebar': 'Открыть боковую панель',
  'common.or': 'или',
  'common.profile': 'Профиль',
  'common.profileImage': 'Изображение профиля',
  'common.serverError': 'Ошибка сервера. Попробуйте позже',
  'header.myProfile': 'Мой профиль',
  'header.profileAndApi': 'Профиль и API',
  'home.newFolder': 'Новая папка',
  'home.projects': 'Проекты',
  'home.refreshPage': 'Обновить страницу',
  'home.searchVideos': 'Поиск по вашим видео',
  'home.somethingWentWrong': 'Что-то пошло не так',
  'home.translateVideoAndAudio': 'Перевод видео и аудио',
  'home.trial.day': 'День {currentDay} из {totalDays}',
  'home.tryRefresh': 'Пожалуйста, обновите страницу',
  'language.english': 'Английский',
  'language.french': 'Французский',
  'language.italian': 'Итальянский',
  'language.label': 'Язык интерфейса',
  'language.portuguese': 'Португальский (Бразилия)',
  'language.portuguesePortugal': 'Португальский (Португалия)',
  'language.russian': 'Русский',
  'language.spanish': 'Испанский',
  'sidebar.bookCall': 'Записаться на звонок с нашей командой',
  'sidebar.dictionary': 'Глоссарий',
  'sidebar.getHelp': 'Помощь',
  'sidebar.helpCenter': 'Справочный центр',
  'sidebar.recordVideo': 'Запись видео',
  'sidebar.settingsMembers': 'Настройки и участники',
  'sidebar.translation': 'Перевод',
  'sidebar.viewPlans': 'Посмотреть тарифы',
  'sidebar.voiceCloneLibrary': 'Библиотека Voice Clone',
  'createProject.autodetect': 'Определить автоматически',
  'createProject.choose': 'Выбрать',
  'createProject.chooseFile': 'Нажмите, чтобы выбрать файл',
  'createProject.chooseFiles': 'Нажмите, чтобы выбрать файлы (не более {count})',
  'createProject.dragIt': 'или перетащите его сюда',
  'createProject.dragThem': 'или перетащите их сюда',
  'createProject.dropFiles': 'Перетащите файлы сюда или нажмите, чтобы загрузить',
  'createProject.fillDetails': 'Укажите параметры проекта',
  'createProject.fillDetailsAndUpload': 'Укажите параметры проекта и загрузите файлы',
  'createProject.freeVideoLeft': '{count} бесплатное видео',
  'createProject.freeVideosLeft': '{count} бесплатных видео',
  'createProject.localizeVideoTitle': 'Локализуйте видео',
  'createProject.newTranslation': 'Новый перевод',
  'createProject.noVideo': 'Нет видео?',
  'createProject.originalLanguage': 'Исходный язык',
  'createProject.pasteLink': 'Вставьте ссылку на YouTube или Google Drive',
  'createProject.pasteLinkWithVimeo': 'Вставьте ссылку на YouTube, Google Drive или Vimeo',
  'createProject.projectName': 'Название проекта',
  'createProject.renewPlan': 'Возобновить подписку',
  'createProject.sampleDescription':
    'Мы уже указали параметры тестового видео. Просто выберите язык, на который хотите его перевести.',
  'createProject.selectLanguages': 'Выберите исходный язык видео и язык перевода',
  'createProject.selectTargetLanguage': 'Выберите язык перевода видео',
  'createProject.translate': 'Перевести',
  'createProject.translateFirstMinute':
    'Мы переведём первую минуту вашего видео. Видео может быть длиннее — пока мы используем только первую минуту.',
  'createProject.translateRecording': 'Переведите запись экрана',
  'createProject.translateTo': 'Перевести на',
  'createProject.trySampleVideo': 'Попробуйте тестовое видео',
  'createProject.upgradePlan': 'Улучшить тариф',
  'createProject.uploadFile': 'Нажмите, чтобы загрузить файл',
  'createProject.uploadTitle': 'Загрузите видео или аудио для перевода',
  'createProject.videoLeft': 'Осталось {count} видео',
  'createProject.videoName': 'Название видео',
  'createProject.videoNames': 'Названия видео',
  'createProject.videosLeft': 'Осталось {count} видео',
  'createProject.videoSpeakers': 'Количество спикеров в видео',
  ...ACCOUNT_TRANSLATIONS.ru,
  ...AUTH_TRANSLATIONS.ru,
  ...BOOSTERS_TRANSLATIONS.ru,
  ...CREATE_PROJECT_TRANSLATIONS.ru,
  ...EDITOR_TRANSLATIONS.ru,
  ...HOME_TRANSLATIONS.ru,
  ...PRICING_TRANSLATIONS.ru,
  ...RECORDING_EDITOR_TRANSLATIONS.ru,
  ...RECORDING_MODALS_TRANSLATIONS.ru,
  ...RECORDS_TRANSLATIONS.ru,
  ...HOME_HUB_TRANSLATIONS.ru,
  ...SIDEBAR_NAV_TRANSLATIONS.ru,
  ...TRANSLATION_SECTION_TRANSLATIONS.ru,
  ...SHELL_TRANSLATIONS.ru,
  ...TRIAL_TRANSLATIONS.ru,
  ...TRIAL_PAYWALL_TRANSLATIONS.ru,
  ...VOICE_TRANSLATIONS.ru,
};

export const TRANSLATIONS = {
  en: EN_TRANSLATIONS,
  es: ES_TRANSLATIONS,
  fr: FR_TRANSLATIONS,
  it: IT_TRANSLATIONS,
  pt: PT_TRANSLATIONS,
  'pt-PT': PT_PT_TRANSLATIONS,
  ru: RU_TRANSLATIONS,
} as const;

const resources = Object.fromEntries(APP_LOCALES.map((locale) => [locale, { translation: TRANSLATIONS[locale] }]));

export const interfaceI18n = createInstance();

void interfaceI18n.init({
  fallbackLng: 'en',
  initAsync: false,
  interpolation: {
    escapeValue: false,
    prefix: '{',
    suffix: '}',
  },
  keySeparator: false,
  lng: getActiveInterfaceLocale(),
  nsSeparator: false,
  react: { useSuspense: false },
  resources,
  returnNull: false,
  returnObjects: false,
  supportedLngs: APP_LOCALES,
});

export const translate = (locale: AppLocale, key: TranslationKey, variables?: Record<string, number | string>) => {
  return interfaceI18n.t(key, { ...variables, lng: locale });
};
